import { createContext, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { SessionTokens } from "./types";
import { clearListMemory } from "./listStateStorage";

const storageKey = "brandwise_platform_admin_session";

type SessionContextValue = {
  accessToken: string | null;
  saveSession: (tokens: SessionTokens) => void;
  clearSession: () => void;
};

const SessionContext = createContext<SessionContextValue | null>(null);

export function getStoredSession(): SessionTokens | null {
  const stored = window.localStorage.getItem(storageKey);
  if (!stored) {
    return null;
  }
  try {
    const parsed = JSON.parse(stored) as SessionTokens;
    if (!parsed.accessToken || !parsed.refreshToken) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function storeSession(tokens: SessionTokens) {
  window.localStorage.setItem(storageKey, JSON.stringify(tokens));
}

export function removeStoredSession() {
  clearListMemory();
  window.localStorage.removeItem(storageKey);
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [tokens, setTokens] = useState<SessionTokens | null>(() => getStoredSession());

  const value = useMemo<SessionContextValue>(() => ({
    accessToken: tokens?.accessToken ?? null,
    saveSession(nextTokens) {
      storeSession(nextTokens);
      setTokens(nextTokens);
    },
    clearSession() {
      removeStoredSession();
      setTokens(null);
    }
  }), [tokens]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used within SessionProvider.");
  }
  return context;
}
